import { numify } from 'numify';
import { toast } from 'sonner';
import { telegramWebApp } from '@/main';
/////////////////////////////////////////////////////////////
import { useCustomDispatch } from '@/core/redux/hooks';
import {
	Drawer,
	DrawerContent,
	DrawerClose,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	DrawerDescription,
	Button,
	Close,
	CoinImage,
} from '@/app/shared/components';
import { RewardService } from '@/core/services';
import { ClaimDailyRewardResponse, DailyReward } from '@/core/interfaces';
import { setCounter, setStickerWonDialog } from '@/core/redux/slices';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

interface DailyRewardProps {
	numDay: number;
	isOpen: boolean;
	refresh: (id: number) => void;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
	reward: DailyReward;
}

export const DailyRewardDetail: React.FC<DailyRewardProps> = ({
	numDay,
	isOpen,
	refresh,
	setIsOpen,
	reward,
}: DailyRewardProps) => {
	/*********************
	// #region Hooks
	**********************/
	const dispatch = useCustomDispatch();
	const { t } = useTranslation();

	/*********************
	// #region Redux
	**********************/

	/*********************
	// #region Functions
	**********************/
	const handleOnClickGoReward = () => {
		telegramWebApp.HapticFeedback.impactOccurred('heavy');
		toast.promise(RewardService.claimDailyReward(), {
			loading: t('general.loading'),
			success: (data: ClaimDailyRewardResponse) => {
				// Check if has a gift
				if (data.isAwardWinning) {
					dispatch(setStickerWonDialog(true));
				}
				dispatch(setCounter(data.currentCoins));
				refresh(numDay);
				return t('dailyReward.collectedMessage');
			},
			error: t('dailyReward.errorMessage'),
		});
	};
	/*********************
	// #region Template
	**********************/
	return (
		<Drawer open={isOpen} onOpenChange={(e) => setIsOpen(e)}>
			<DrawerContent>
				<div className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" />
				<div className="mx-auto w-full max-w-sm ">
					<DrawerHeader>
						<DrawerTitle>
							<VisuallyHidden>
								{t('general.day')} {numDay}
							</VisuallyHidden>
							<DrawerClose className="right-5 text-right absolute">
								<Close fillcolor="#fff" />
							</DrawerClose>
						</DrawerTitle>
					</DrawerHeader>

					<DrawerDescription>
						<VisuallyHidden>
							{t('general.day')} {numDay}
						</VisuallyHidden>
					</DrawerDescription>

					<div className="mx-auto px-8 flerx flex-col">
						<div className="flex flex-col my-4 justify-center items-center gap-5">
							<p className="px-5 text-2xl font-bold text-center justify-center">
								{t('general.day')} {numDay}
							</p>
							{reward.rewardType === 1 ? (
								<img
									src={
										process.env.VITE_DAILY_REWARD_PAGE_IMAGE
									}
									alt="Reward"
									width={130}
									height={94}
								/>
							) : (
								<img
									src={process.env.VITE_DIAMOND_IMAGE}
									alt="Reward"
									width={200}
									className="drop-shadow-lg "
								/>
							)}
						</div>

						<div className="flex mb-8 gap-2 justify-center items-center">
							{reward.rewardType === 1 && (
								<CoinImage width={48} />
							)}

							<p className="font-bold text-3xl">
								{numify(reward.reward)}
							</p>
						</div>
					</div>

					<DrawerFooter>
						<DrawerClose asChild>
							<Button
								variant="primary"
								className="py-6 w-full flex items-center gap-1 text-sm rounded-lg"
								onClick={handleOnClickGoReward}
							>
								{t('general.claim')}
							</Button>
						</DrawerClose>
					</DrawerFooter>
				</div>
			</DrawerContent>
		</Drawer>
	);
};
